import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { ADMIN_API_PREFIX, BASE_URL } from '@CONSTANTS/API.constant';
import {
    AdminSingleSectionData,
    AdminSingleSectionMeta,
    AdminSingleSectionMetaSection,
} from '@INTERFACES/admin-api/sections';
import { AdminSingleChapterDataSection } from '@INTERFACES/admin-api/chapters';

import userRepository from '@REPOSITORIES/local-repository/user-repository';

import {
    chaptersAdminApi,
    documentsAdminApi,
    extractSectionsFilters,
    extractSectionsTags,
    formatVersion, tagsAdminApi,
    unpublishedAdminApi,
} from '@SERVICES';
import { DOCUMENT_PERMISSIONS } from '@CONSTANTS/PERMISSIONS.constant';
import invalidateData from '../helpers/invalidateData';

type SectionParent = {
    id: number;
    name: string;
    parent: SectionParent | null;
};

type Section = {
    id: number;
    name: string;
    order: number;
    cover: string | null;
    document: {
        id: number;
        name: string;
        owner: number;
    };
    chapter: {
        id: number;
        name: string;
    };
    is_mandatory: boolean;
    is_page_break: boolean;
    is_landscape: boolean;
    mandatory_conditions: {
        id: number;
        brand: {
            id: number;
            name: string;
        }[] | null;
        region: {
            id: number;
            name: string;
        }[] | null;
        area: {
            id: number;
            name: string;
        }[] | null;
        country: {
            id: number;
            name: string;
        }[] | null;
        property_type: {
            id: number;
            name: string;
        }[] | null;
    }[];
    base_text: {
        body: string;
        is_published: boolean;
        versions: {
            id: number;
            description: string;
            body: string;
            created_at: string;
            author: {
                id: number;
                full_name: string;
                email: string;
            };
        }[];
    }
    tags: {
        id: number;
        name: string;
    }[];
    vendors: {
        id: number;
        name: string;
        company: string;
        address: string;
        email: string;
        phone: string;
        is_visible: boolean;
    }[];
    variants: {
        id: number;
        name: string;
        is_default: boolean;
        is_visible: boolean;
        is_published: boolean;
        brands: {
            id: number;
            name: string;
        }[];
        regions: {
            id: number;
            name: string;
        }[];
        areas: {
            id: number;
            name: string;
        }[];
        countries: {
            id: number;
            name: string;
        }[];
        property_types: {
            id: number;
            name: string;
        }[];
        body: string;
        tags: {
            id: number;
            name: string;
        }[];
        vendors: {
            id: number;
            name: string;
            company: string;
            address: string;
            email: string;
            phone: string;
            is_visible: boolean;
        }[];
        versions: {
            id: number;
            description: string;
            body: string;
            created_at: string;
            author: {
                id: number;
                full_name: string;
                email: string;
            };
        }[];
    }[];
    parent: SectionParent | null;
    children: Section[] | []
};

type SectionMeta = {
    id: number;
    name: string;
    variants: {
        id: number;
        name: string;
    }[];
    parent_id: number | null;
    children: SectionMeta[] | [];
};

type GetByIdApiResponse = {
    data: Section,
    meta: {
        sections: SectionMeta[];
        options: {
            brands: {
                id: number;
                name: string;
            }[];
            regions: {
                id: number;
                name: string;
            }[];
            areas: {
                id: number;
                name: string;
                regions: {
                    id: number;
                    name: string;
                }[];
            }[];
            countries: {
                id: number;
                name: string;
                region: {
                    id: number;
                    name: string;
                } | null;
                areas: {
                    id: number;

                    name: string;
                }[];
            }[];
            property_types: {
                id: number;
                name: string;
            }[];
        };
        permissions: DOCUMENT_PERMISSIONS[];
    },
};

type SectionHistory = {
    data: SectionHistoryData;
};

export type SectionHistoryData = {
    id: number;
    name: string;
    isLandscape: boolean;
    baseText: SectionHistoryBaseText;
    variants: SectionHistoryVariant[];
};

export type SectionHistoryBaseText = {
    isPublished: boolean;
    versions: SectionHistoryVersion[];
};

export type SectionHistoryVariant = {
    id: number;
    name: string;
    isDefault: boolean;
    isPublished: boolean;
    brands: {
        id: number;
        name: string;
    }[];
    regions: {
        id: number;
        name: string;
    }[];
    areas: {
        id: number;
        name: string;
    }[];
    countries: {
        id: number;
        name: string;
    }[];
    propertyTypes: {
        id: number;
        name: string;
    }[];
    versions: SectionHistoryVersion[];
};

type SectionHistoryVersion = {
    id: number;
    body: string;
    description: string;
    createdAt: string;
    author: {
        id: number;
        fullName: string;
        email: string;
    }
};

type SectionHistoryApiResponse = {
    id: number;
    name: string;
    is_landscape: boolean;
    base_text: {
        is_published: boolean;
        versions: {
            id: number;
            description: string;
            body: string;
            created_at: string;
            author: {
                id: number;
                full_name: string;
                email: string;
            };
        }[];
    }
    variants: {
        id: number;
        name: string;
        is_default: boolean;
        is_published: boolean;
        brands: {
            id: number;
            name: string;
        }[];
        regions: {
            id: number;
            name: string;
        }[];
        areas: {
            id: number;
            name: string;
        }[];
        countries: {
            id: number;
            name: string;
        }[];
        property_types: {
            id: number;
            name: string;
        }[];
        versions: {
            id: number;
            body: string;
            description: string;
            created_at: string;
            author: {
                id: number;
                full_name: string;
                email: string;
            }
        }[];
    }[];
};

type CreateParams = {
    documentId: number;
    chapterId: number;
    parent: number | null;
    name: string;
    currentSection: number | null;
};

type CreateApiResponse = {
    id: number;
    name: string;
    base_text: {
        body: string;
    };
    is_mandatory: boolean;
    is_page_break: boolean;
    is_landscape: boolean;
    has_mandatory_conditions: boolean;
    cover: string | null;
    order: number;
    parent: number | null
};

type UpdateParams = {
    id: number;
    name: string;
    body: string;
    isMandatory: boolean;
    isPageBreak: boolean;
    isLandscape: boolean;
    chapterId: number;
    documentId: number;
};

type DeleteParams = {
    id: number;
    documentId: number;
    chapterId: number;
};

type CopyApiResponse = {
    id: number;
    name: string;
    order: number;
    is_mandatory: boolean;
    is_page_break: boolean;
    is_landscape: boolean;
    has_mandatory_conditions: boolean;
    base_text: {
        body: string;
        is_published: boolean;
        version: {
            id: number;
            description: string;
            body: string;
            created_at: string;
            author: {
                id: number;
                full_name: string;
                email: string;
            };
        } | null;
    };
    tags: {
        id: number;
        name: string;
    }[];
    variants: {
        id: number;
        name: string;
        body: string;
        is_default: boolean;
        is_published: boolean;
        version: {
            id: number;
            description: string;
            body: string;
            created_at: string;
            author: {
                id: number;
                full_name: string;
                email: string;
            };
        } | null;
        brands: {
            id: number;
            name: string;
        }[];
        regions: {
            id: number;
            name: string;
        }[];
        areas: {
            id: number;
            name: string;
        }[];
        countries: {
            id: number;
            name: string;
        }[];
        property_types: {
            id: number;
            name: string;
        }[];
        tags: {
            id: number;
            name: string;
        }[]
    }[];
};

type CopyParams = {
    documentId: number;
    chapterId: number;
    id: number;
};

type AddVendorApiResponse = {
    vendor_id: number;
    vendor_name: string;
    vendor_company: string;
    vendor_address: string;
    vendor_email: string;
    vendor_phone: string;
    vendor_is_visible: boolean;
};

type AddVendorParams = {
    id: number;
    vendorId: number;
};

type DeleteVendorParams = {
    id: number;
    vendorId: number;
};

type CreateAndAddTagParams = {
    documentId: number;
    chapterId: number;
    id: number;
    tagName: string;
};

type AddTagApiResponse = {
    tag_id: number;
    tag_name: string;
};

type AddTagParams = {
    chapterId: number;
    id: number;
    tagId: number;
};

type DeleteTagParams = {
    chapterId: number;
    id: number;
    tagId: number;
};

const SortById = (a: any, b: any) => a.id - b.id;
const SortByOrder = (a: any, b: any) => a.order - b.order;

function updateSingleSectionTags(
    {
        dispatch, id, data,
    }: {
        dispatch: ThunkDispatch<any, any, AnyAction>;
        id: number;
        data: {
            tagId: number;
            tagName: string;
        }
    },
) {
    dispatch(
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        sectionsAdminApi.util.updateQueryData('getById', id, (draft) => {
            const tags = [...draft.data.tags, {
                id: data.tagId,
                name: data.tagName,
            }];

            tags.sort(SortById);

            return ({
                data: {
                    ...draft.data,
                    tags,
                },
                meta: draft.meta,
            });
        }),
    );
}

function updateSingleChapterTags(
    {
        dispatch, id, chapterId, data,
    }: {
        dispatch: ThunkDispatch<any, any, AnyAction>;
        chapterId: number;
        id: number;
        data: {
            tagId: number;
            tagName: string;
        }
    },
) {
    dispatch(
        chaptersAdminApi.util.updateQueryData('getById', chapterId, (draft) => ({
            data: {
                ...draft.data,
                sections: draft.data.sections.map((section) => {
                    if (section.id === id) {
                        const tags = [...section.tags, {
                            id: data.tagId,
                            name: data.tagName,
                        }];

                        tags.sort(SortById);

                        return {
                            ...section,
                            tags,
                        };
                    }

                    return section;
                }),
            },
            meta: {
                ...draft.meta,
                tags: extractSectionsTags(draft.data.sections.reduce((acc: any, section: any) => {
                    const { variants } = section;

                    return [
                        ...acc,
                        ...[...section.tags, {
                            id: data.tagId,
                            name: data.tagName,
                        }],
                        ...variants.reduce((innerAcc: any, variant: any) => {
                            const { tags } = variant;

                            return [...innerAcc, ...tags];
                        }, []),
                    ];
                }, [])),
            },
        })),
    );
}

type ReturnType = {
    data: AdminSingleSectionData;
    meta: AdminSingleSectionMeta,
};

const transformSectionData = (raw: any): AdminSingleSectionData => ({
    id: raw.id,
    name: raw.name,
    order: raw.order,
    cover: raw.cover,
    document: raw.document,
    chapter: raw.chapter,
    isMandatory: raw.is_mandatory,
    isPageBreak: raw.is_page_break,
    isLandscape: raw.is_landscape,
    mandatoryConditions: raw.mandatory_conditions.map((con: any) => ({
        id: con.id,
        brand: con.brand,
        region: con.region,
        area: con.area,
        country: con.country,
        propertyType: con.property_type,
    })),
    baseText: {
        body: raw.base_text.body || '',
        isPublished: raw.base_text.is_published,
        versions: raw.base_text.versions.map((version: any) => formatVersion(version)),
    },
    tags: raw.tags.map((tag: any) => ({
        id: tag.id,
        name: tag.name,
    })).sort(SortById),
    vendors: raw.vendors.map((vendor: any) => ({
        id: vendor.id,
        name: vendor.name,
        company: vendor.company,
        address: vendor.address,
        email: vendor.email,
        phone: vendor.phone,
        isVisible: vendor.is_visible,
    })),
    variants: raw.variants.map((variant: any) => ({
        id: variant.id,
        name: variant.name,
        isDefault: variant.is_default,
        isVisible: variant.is_visible,
        isPublished: variant.is_published,
        brands: variant.brands,
        regions: variant.regions,
        areas: variant.areas,
        countries: variant.countries,
        propertyTypes: variant.property_types,
        body: variant.body || '',
        tags: variant.tags.map((tag: any) => ({
            id: tag.id,
            name: tag.name,
        })).sort(SortById),
        vendors: variant.vendors.map((vendor: any) => ({
            id: vendor.id,
            name: vendor.name,
            company: vendor.company,
            address: vendor.address,
            email: vendor.email,
            phone: vendor.phone,
            isVisible: vendor.is_visible,
        })),
        versions: variant.versions.map((version: any) => formatVersion(version)),
    })),
    parent: raw.parent,
    children: raw.children && raw.children.length > 0
        ? raw.children.map((child: any) => transformSectionData(child))
        : [],
});

const transformSectionMetaData = (raw: any) : any => ({
    id: raw.id,
    name: raw.name,
    variants: raw.variants.map((variant: any) => ({
        id: variant.id,
        name: variant.name,
    })),
    parent_id: raw.parent_id || null,
    children: raw.children && raw.children.length > 0
        ? raw.children.map((child: any) => transformSectionMetaData(child))
        : [],

});

function transformGetByIdResponse(raw: GetByIdApiResponse): ReturnType {
    const data: AdminSingleSectionData = {
        id: raw.data.id,
        name: raw.data.name,
        order: raw.data.order,
        cover: raw.data.cover,
        document: raw.data.document,
        chapter: raw.data.chapter,
        isMandatory: raw.data.is_mandatory,
        isPageBreak: raw.data.is_page_break,
        isLandscape: raw.data.is_landscape,
        mandatoryConditions: raw.data.mandatory_conditions.map((con) => ({
            id: con.id,
            brand: con.brand,
            region: con.region,
            area: con.area,
            country: con.country,
            propertyType: con.property_type,
        })),
        baseText: {
            body: raw.data.base_text.body || '',
            isPublished: raw.data.base_text.is_published,
            versions: raw.data.base_text.versions.map((version) => formatVersion(version)),
        },
        tags: raw.data.tags.map((tag) => ({
            id: tag.id,
            name: tag.name,
        })).sort(SortById),
        vendors: raw.data.vendors.map((vendor) => ({
            id: vendor.id,
            name: vendor.name,
            company: vendor.company,
            address: vendor.address,
            email: vendor.email,
            phone: vendor.phone,
            isVisible: vendor.is_visible,
        })),
        variants: raw.data.variants.map((variant) => ({
            id: variant.id,
            name: variant.name,
            isDefault: variant.is_default,
            isVisible: variant.is_visible,
            isPublished: variant.is_published,
            brands: variant.brands,
            regions: variant.regions,
            areas: variant.areas,
            countries: variant.countries,
            propertyTypes: variant.property_types,
            body: variant.body || '',
            tags: variant.tags.map((tag) => ({
                id: tag.id,
                name: tag.name,
            })).sort(SortById),
            vendors: variant.vendors.map((vendor) => ({
                id: vendor.id,
                name: vendor.name,
                company: vendor.company,
                address: vendor.address,
                email: vendor.email,
                phone: vendor.phone,
                isVisible: vendor.is_visible,
            })),
            versions: variant.versions.map((version) => formatVersion(version)),
        })),
        parent: raw.data.parent,
        children: raw.data.children && raw.data.children.length > 0
            ? raw.data.children.map((child: any) => transformSectionData(child))
            : [],
    };

    const meta = {
        sections: raw.meta.sections.map((section) => ({
            id: section.id,
            name: section.name,
            variants: section.variants.map((variant) => ({
                id: variant.id,
                name: variant.name,
            })),
            parent_id: section.parent_id,
            children: section.children && section.children.length > 0
                ? section.children.map((child: any) => transformSectionMetaData(child))
                : [],
        })),
        options: {
            brands: raw.meta.options.brands.map((brand) => ({
                id: brand.id,
                name: brand.name,
            })),
            regions: raw.meta.options.regions.map((region) => ({
                id: region.id,
                name: region.name,
            })),
            areas: raw.meta.options.areas.map((area) => ({
                id: area.id,
                name: area.name,
                regions: area.regions.map((region) => ({
                    id: region.id,
                    name: region.name,
                })),
            })),
            countries: raw.meta.options.countries.map((country) => ({
                id: country.id,
                name: country.name,
                region: country.region,
                areas: country.areas.map((area) => ({
                    id: area.id,
                    name: area.name,
                })),
            })),
            propertyTypes: raw.meta.options.property_types.map((propertyType) => ({
                id: propertyType.id,
                name: propertyType.name,
            })),
        },
        permissions: raw.meta.permissions,
    };

    return {
        data,
        meta,
    };
}

function transformHistoryResponse(raw: SectionHistoryApiResponse): SectionHistory {
    return {
        data: {
            id: raw.id,
            name: raw.name,
            isLandscape: raw.is_landscape,
            baseText: {
                isPublished: raw.base_text.is_published,
                versions: raw.base_text.versions.map((version) => formatVersion(version)),
            },
            variants: raw.variants.map((variant) => ({
                id: variant.id,
                name: variant.name,
                isDefault: variant.is_default,
                isPublished: variant.is_published,
                brands: variant.brands,
                regions: variant.regions,
                areas: variant.areas,
                countries: variant.countries,
                propertyTypes: variant.property_types,
                versions: variant.versions.map((version) => formatVersion(version)),
            })),
        },
    };
}

export const sectionsAdminApi = createApi({
    reducerPath: 'sections-admin-api',
    baseQuery: fetchBaseQuery({
        baseUrl: BASE_URL + ADMIN_API_PREFIX,
        prepareHeaders: (headers) => {
            const token = userRepository.getData();

            if (token) {
                headers.set('Authorization', `Bearer ${token}`);
            }

            return headers;
        },
    }),
    tagTypes: ['Section-History'],
    endpoints: (builder) => ({
        getById: builder.query<ReturnType, number>({
            query: (id: number) => `/sections/${id}/`,
            transformResponse(raw: GetByIdApiResponse) {
                return transformGetByIdResponse(raw);
            },
        }),
        history: builder.query<SectionHistory, number>({
            query: (id: number) => ({
                url: `/sections/${id}/history/`,
            }),
            transformResponse(raw: SectionHistoryApiResponse) {
                return transformHistoryResponse(raw);
            },
            providesTags: (result) => (result ? [{
                type: 'Section-History' as const,
                id: result.data.id,
            }] : [{ type: 'Section-History' as const, id: 'history' }]),
        }),
        create: builder.mutation<CreateApiResponse, CreateParams>({
            query: (params) => {
                const formData = new FormData();

                formData.append('chapter', params.chapterId.toString());
                formData.append('name', params.name);
                formData.append('parent', params.parent?.toString() || '');

                return {
                    url: '/sections/',
                    method: 'POST',
                    body: formData,
                };
            },
            async onQueryStarted(obj, { dispatch, queryFulfilled }) {
                try {
                    const {
                        documentId, chapterId, parent, currentSection,
                    } = obj;
                    const { data: queryData }: { data: CreateApiResponse } = await queryFulfilled;

                    // INVALIDATE DATA
                    invalidateData(dispatch);

                    dispatch(
                        unpublishedAdminApi.util.resetApiState(),
                    );

                    // ADD SECTION TO SINGLE DOCUMENT

                    dispatch(
                        documentsAdminApi.util.updateQueryData('getById', documentId, (draft) => {
                            const addSectionToChildren = (sections: any[], parentId: any, newSection: any): any[] => sections.map((section: any) => {
                                if (section.id === parentId) {
                                    return {
                                        ...section,
                                        children: [
                                            ...section.children,
                                            newSection, // Add the new section
                                        ],
                                    };
                                }

                                // If this section has children, recursively update them
                                if (section.children && section.children.length > 0) {
                                    return {
                                        ...section,
                                        children: addSectionToChildren(section.children, parentId, newSection),
                                    };
                                }

                                // If no changes are needed, return the section as is
                                return section;
                            });

                            return {
                                data: {
                                    ...draft.data,
                                    chapters: draft.data.chapters.map((chapter) => {
                                        if (chapter.id === chapterId) {
                                            const newSection = {
                                                id: queryData.id,
                                                name: queryData.name,
                                                baseText: {
                                                    isPublished: false,
                                                },
                                                variants: [],
                                                parent: null,
                                                children: [],
                                                order: queryData.order,
                                            };
                                            let updatedSections = [...chapter.sections];

                                            if (parent) {
                                                updatedSections = addSectionToChildren(updatedSections, parent, newSection);
                                            } else {
                                                updatedSections.push(newSection);
                                            }

                                            return {
                                                ...chapter,
                                                sections: updatedSections,
                                            };
                                        }

                                        return chapter;
                                    }),
                                },
                                meta: draft.meta,
                            };
                        }),
                    );

                    // ADD SECTION TO SINGLE CHAPTER
                    const chapterPatch = dispatch(
                        chaptersAdminApi.util.updateQueryData('getById', chapterId, (draft) => {
                            const addSectionToChildren = (sections: any[], parentId: any, newSection: any): any[] => sections.map((section: any) => {
                                if (section.id === parentId) {
                                    return {
                                        ...section,
                                        children: [
                                            ...section.children,
                                            newSection,
                                        ],
                                    };
                                }

                                if (section.children && section.children.length > 0) {
                                    return {
                                        ...section,
                                        children: addSectionToChildren(section.children, parentId, newSection),
                                    };
                                }

                                return section;
                            });

                            const newSection = {
                                id: queryData.id,
                                name: queryData.name,
                                order: queryData.order,
                                isMandatory: queryData.is_mandatory,
                                isPageBreak: queryData.is_page_break,
                                isLandscape: queryData.is_landscape,
                                hasMandatoryConditions: queryData.has_mandatory_conditions,
                                baseText: {
                                    body: '',
                                    isPublished: false,
                                    latestVersion: null,
                                },
                                tags: [],
                                variants: [],
                                parent: null,
                                children: [],
                            };

                            let updatedSections = [...draft.data.sections];

                            if (parent) {
                                updatedSections = addSectionToChildren(updatedSections, parent, newSection);
                            } else {
                                updatedSections.push(newSection);
                            }

                            return {
                                data: {
                                    ...draft.data,
                                    sections: updatedSections,
                                },
                                meta: draft.meta,
                            };
                        }),
                    );

                    const data = chapterPatch.patches[0]?.value;

                    if (data) {
                        // ADD SECTION TO EVERY SINGLE SECTION META
                        data.data.sections.forEach((section: AdminSingleSectionMetaSection) => {
                            dispatch(
                                sectionsAdminApi.util.updateQueryData('getById', section.id, (draft) => ({
                                    data: draft.data,
                                    meta: {
                                        ...draft.meta,
                                        sections: [...draft.meta.sections, {
                                            id: queryData.id,
                                            name: queryData.name,
                                            variants: [],
                                            children: [],
                                        }],
                                    },
                                })),
                            );
                        });
                    }

                    // UPDATE THE SECTION
                    if (currentSection) {
                        dispatch(
                            sectionsAdminApi.util.updateQueryData('getById', currentSection, (draft) => {
                                const addSectionToChildren = (sections: any[], parentId: any, newSection: any): any[] => sections.map((section: any) => {
                                    if (section.id === parentId) {
                                        return {
                                            ...section,
                                            children: [
                                                ...section.children,
                                                newSection,
                                            ],
                                        };
                                    }

                                    if (section.children && section.children.length > 0) {
                                        return {
                                            ...section,
                                            children: addSectionToChildren(section.children, parentId, newSection),
                                        };
                                    }

                                    return section;
                                });
                                const newSection = {
                                    id: queryData.id,
                                    name: queryData.name,
                                    order: queryData.order,
                                    isMandatory: queryData.is_mandatory,
                                    isPageBreak: queryData.is_page_break,
                                    isLandscape: queryData.is_landscape,
                                    hasMandatoryConditions: queryData.has_mandatory_conditions,
                                    baseText: {
                                        body: '',
                                        isPublished: false,
                                        versions: [],
                                    },
                                    tags: [],
                                    variants: [],
                                    parent: {
                                        id: draft.data.id,
                                        name: draft.data.name,
                                        parent: null,
                                    },
                                    children: [],
                                    cover: null,
                                    document: draft.data.document,
                                    chapter: draft.data.chapter,
                                    mandatoryConditions: [],
                                    vendors: [],
                                };

                                const newMetaSection = {
                                    id: queryData.id,
                                    name: queryData.name,
                                    variants: [],
                                    parent_id: draft.data.id,
                                    children: [],
                                };

                                let updatedSections = [...draft.data.children];
                                let updatedMetaSections = [...draft.meta.sections];
                                if (parent === draft.data.id) {
                                    updatedSections.push(newSection);
                                } else {
                                    updatedSections = addSectionToChildren(updatedSections, parent, newSection);
                                }

                                updatedMetaSections = addSectionToChildren(updatedMetaSections, parent, newMetaSection);

                                return {
                                    data: {
                                        ...draft.data,
                                        children: updatedSections,
                                    },
                                    meta: { ...draft.meta, sections: updatedMetaSections },
                                };
                            }),
                        );
                    }
                } catch (e) {
                    //
                }
            },
        }),
        update: builder.mutation({
            query: (params: UpdateParams) => {
                const formData = new FormData();

                formData.append('name', params.name);
                formData.append('body', params.body);
                formData.append('is_mandatory', params.isMandatory.toString() || '');
                formData.append('is_page_break', params.isPageBreak.toString() || '');
                formData.append('is_landscape', params.isLandscape.toString() || '');

                return {
                    url: `/sections/${params.id}/`,
                    method: 'PATCH',
                    body: formData,
                };
            },
            // Don't invalidate any tags - we handle cache updates manually
            invalidatesTags: [],
            async onQueryStarted(
                {
                    id, chapterId, documentId, body,
                },
                { dispatch, queryFulfilled },
            ) {
                // Normalize line endings to match API response
                const normalizedBody = body?.replace(/\n/g, '\r\n') || '';
                
                // OPTIMISTIC UPDATE - Update section cache immediately
                const sectionPatchUndo = dispatch(
                    sectionsAdminApi.util.updateQueryData('getById', id, (draft) => ({
                        ...draft,
                        data: {
                            ...draft.data,
                            baseText: {
                                ...draft.data.baseText,
                                body: normalizedBody,
                            },
                        },
                    }))
                );

                try {
                    const { data: queryData } = await queryFulfilled;

                    // Only reset unpublished state - don't invalidate sections cache
                    dispatch(
                        unpublishedAdminApi.util.resetApiState(),
                    );

                    // Helper function to recursively update section and its children
                    const updateSectionInChildren = (sections: any[], sectionId: string, updatedData: any) => sections.map((section: any): any => {
                        if (Number(section.id) === Number(sectionId)) {
                            return {
                                ...section,
                                ...updatedData,
                            };
                        }

                        if (section.children && section.children.length > 0) {
                            return {
                                ...section,
                                children: updateSectionInChildren(section.children, sectionId, updatedData),
                            };
                        }

                        return section;
                    });

                    // UPDATE SECTION IN SINGLE DOCUMENT
                    dispatch(
                        documentsAdminApi.util.updateQueryData('getById', documentId, (draft) => ({
                            data: {
                                ...draft.data,
                                chapters: draft.data.chapters.map((chapter) => {
                                    if (chapter.id === chapterId) {
                                        return {
                                            ...chapter,
                                            sections: updateSectionInChildren(chapter.sections, id.toString(), {
                                                name: queryData.name,
                                                baseText: {
                                                    ...queryData.baseText,
                                                    isPublished: queryData.is_published,
                                                },
                                            }),
                                        };
                                    }

                                    return chapter;
                                }),
                            },
                            meta: draft.meta,
                        })),
                    );

                    // UPDATE SECTION IN SINGLE CHAPTER
                    dispatch(
                        chaptersAdminApi.util.updateQueryData('getById', chapterId, (draft) => ({
                            data: {
                                ...draft.data,
                                sections: updateSectionInChildren(draft.data.sections, id.toString(), {
                                    name: queryData.name,
                                    isMandatory: queryData.is_mandatory,
                                    isPageBreak: queryData.is_page_break,
                                    isLandscape: queryData.is_landscape,
                                    baseText: {
                                        ...((draft.data as { baseText?: { body: string; isPublished: boolean } }).baseText || { body: '', isPublished: false }),
                                    },

                                }),
                            },
                            meta: draft.meta,
                        })),
                    );

                    // Update cache with server response to ensure consistency
                    const serverBody = queryData.body || normalizedBody;
                    
                    // UPDATE SECTION IN CURRENT SINGLE SECTION
                    const sectionPatch = dispatch(
                        sectionsAdminApi.util.updateQueryData('getById', id, (draft) => ({
                            data: {
                                ...draft.data,
                                name: queryData.name,
                                isMandatory: queryData.is_mandatory,
                                isPageBreak: queryData.is_page_break,
                                isLandscape: queryData.is_landscape,
                                baseText: {
                                    ...draft.data.baseText,
                                    body: serverBody,
                                    isPublished: false,
                                },
                            },
                            meta: draft.meta,
                        })),
                    );

                    const data = sectionPatch.patches[0]?.value;

                    if (data) {
                        // UPDATE SECTION IN EVERY SINGLE SECTION META
                        data.meta.sections.forEach((section: AdminSingleSectionMetaSection) => {
                            dispatch(
                                sectionsAdminApi.util.updateQueryData('getById', section.id, (draft) => ({
                                    data: draft.data,
                                    meta: {
                                        ...draft.meta,
                                        sections: draft.meta.sections.map((s) => {
                                            if (s.id === id) {
                                                return {
                                                    ...s,
                                                    name: queryData.name,
                                                };
                                            }

                                            return s;
                                        }),
                                    },
                                })),
                            );
                        });
                    }
                } catch (e) {
                    // Undo optimistic update on error
                    sectionPatchUndo.undo();
                }
            },
        }),
        delete: builder.mutation(({
            query: (params: DeleteParams) => ({
                url: `/sections/${params.id}/`,
                method: 'DELETE',
            }),
            async onQueryStarted({ id, documentId, chapterId }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    // INVALIDATE DATA
                    invalidateData(dispatch);

                    dispatch(
                        unpublishedAdminApi.util.resetApiState(),
                    );

                    dispatch(
                        sectionsAdminApi.util.resetApiState(),
                    );

                    // Helper function to recursively remove the section from nested children
                    const removeSectionFromChildren = (sections: any[], sectionId: number): any[] => sections.reduce((acc: any[], section: any) => {
                        if (Number(section.id) === Number(sectionId)) {
                            return acc;
                        }

                        if (section.children && section.children.length > 0) {
                            return [
                                ...acc,
                                {
                                    ...section,
                                    children: removeSectionFromChildren(section.children, sectionId),
                                },
                            ];
                        }

                        return [...acc, section];
                    }, []);

                    // DELETE SECTION FROM SINGLE DOCUMENT
                    dispatch(
                        documentsAdminApi.util.updateQueryData('getById', documentId, (draft) => ({
                            data: {
                                ...draft.data,
                                chapters: draft.data.chapters.map((chapter) => {
                                    if (chapter.id === chapterId) {
                                        return {
                                            ...chapter,
                                            sections: removeSectionFromChildren(chapter.sections, id),
                                        };
                                    }

                                    return chapter;
                                }),
                            },
                            meta: draft.meta,
                        })),
                    );

                    // DELETE SECTION FROM SINGLE CHAPTER
                    const chapterPatch = dispatch(
                        chaptersAdminApi.util.updateQueryData('getById', chapterId, (draft) => ({
                            data: {
                                ...draft.data,
                                sections: removeSectionFromChildren(draft.data.sections, id),
                            },
                            meta: {
                                ...draft.meta,
                                tags: extractSectionsTags(draft.data.sections
                                    .filter((s) => s.id !== id)
                                    .reduce((acc: any, section: any) => {
                                        const { variants } = section;

                                        return [
                                            ...acc,
                                            ...section.tags,
                                            ...variants.reduce((innerAcc: any, variant: any) => {
                                                const { tags } = variant;

                                                return [...innerAcc, ...tags];
                                            }, []),
                                        ];
                                    }, [])),
                                filters: extractSectionsFilters(draft.data.sections
                                    .filter((s) => s.id !== id)
                                    .reduce((acc: any, section: any) => {
                                        const { variants } = section;

                                        return [...acc, ...variants.reduce((innerAcc: any, variant: any) => [...innerAcc, {
                                            brands: variant.brands,
                                            regions: variant.regions,
                                            areas: variant.areas,
                                            countries: variant.countries,
                                            propertyTypes: variant.propertyTypes,
                                        }], [])];
                                    }, [])),
                            },
                        })),
                    );

                    const data = chapterPatch.patches[0]?.value;

                    if (data) {
                        // DELETE SECTION FROM EVERY SINGLE SECTION META
                        data.data.sections.forEach((section: AdminSingleSectionMetaSection) => {
                            dispatch(
                                sectionsAdminApi.util.updateQueryData('getById', section.id, (draft) => ({
                                    data: draft.data,
                                    meta: {
                                        ...draft.meta,
                                        sections: draft.meta.sections.filter((s) => s.id !== id),
                                    },
                                })),
                            );
                        });
                    }
                } catch (e) { /**/
                }
            },
            invalidatesTags: (result, error, arg) => [{ type: 'Section-History', id: arg.id }],
        })),
        copy: builder.mutation<CopyApiResponse, CopyParams>({
            query: (params) => ({
                url: `/sections/${params.id}/copy/`,
                method: 'POST',
            }),
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                const {
                    documentId,
                    chapterId,
                } = args;

                try {
                    const { data: queryData } = await queryFulfilled;

                    // INVALIDATE DATA
                    invalidateData(dispatch);

                    dispatch(
                        unpublishedAdminApi.util.resetApiState(),
                    );

                    // COPY SECTION IN SINGLE DOCUMENT
                    dispatch(
                        documentsAdminApi.util.updateQueryData('getById', documentId, (draft) => ({
                            data: {
                                ...draft.data,
                                chapters: draft.data.chapters.map((chapter) => {
                                    if (chapter.id === chapterId) {
                                        return {
                                            ...chapter,
                                            sections: [
                                                ...chapter.sections,
                                                {
                                                    id: queryData.id,
                                                    name: queryData.name,
                                                    baseText: {
                                                        isPublished: queryData.base_text.is_published,
                                                    },
                                                    variants: queryData.variants.map((variant) => ({
                                                        id: variant.id,
                                                        isPublished: variant.is_published,
                                                    })),
                                                    parent: null,
                                                    children: [],
                                                    order: queryData.order,
                                                },
                                            ].sort(SortByOrder),
                                        };
                                    }

                                    return chapter;
                                }),
                            },
                            meta: draft.meta,
                        })),
                    );

                    // COPY SECTION IN SINGLE CHAPTER
                    const sectionPatch = dispatch(
                        chaptersAdminApi.util.updateQueryData('getById', chapterId, (draft) => ({
                            data: {
                                ...draft.data,
                                sections: [
                                    ...draft.data.sections,
                                    {
                                        id: queryData.id,
                                        name: queryData.name,
                                        order: queryData.order,
                                        isMandatory: queryData.is_mandatory,
                                        isPageBreak: queryData.is_page_break,
                                        isLandscape: queryData.is_landscape,
                                        hasMandatoryConditions: queryData.has_mandatory_conditions,
                                        baseText: {
                                            body: queryData.base_text.body || '',
                                            isPublished: queryData.base_text.is_published,
                                            latestVersion: queryData.base_text.version ? formatVersion(queryData.base_text.version) : null,
                                        },
                                        tags: queryData.tags.map((tag) => ({
                                            id: tag.id,
                                            name: tag.name,
                                        })).sort(SortById),
                                        variants: queryData.variants.map((variant) => ({
                                            id: variant.id,
                                            name: variant.name,
                                            body: variant.body || '',
                                            isDefault: variant.is_default,
                                            isPublished: variant.is_published,
                                            latestVersion: variant.version ? formatVersion(variant.version) : null,
                                            brands: variant.brands,
                                            regions: variant.regions,
                                            areas: variant.areas,
                                            countries: variant.countries,
                                            propertyTypes: variant.property_types,
                                            tags: variant.tags.map((tag) => ({
                                                id: tag.id,
                                                name: tag.name,
                                            })).sort(SortById),
                                        })),
                                        parent: null,
                                        children: [],
                                    },
                                ].sort(SortByOrder),
                            },
                            meta: draft.meta,
                        })),
                    );

                    const data = sectionPatch.patches[0]?.value;

                    if (data) {
                        // COPY SECTION IN EVERY SINGLE SECTION META
                        data.data.sections.forEach((section: AdminSingleChapterDataSection) => {
                            dispatch(
                                sectionsAdminApi.util.updateQueryData('getById', section.id, (draft) => ({
                                    data: draft.data,
                                    meta: {
                                        ...draft.meta,
                                        sections: [
                                            ...draft.meta.sections,
                                            {
                                                id: queryData.id,
                                                name: queryData.name,
                                                variants: queryData.variants.map((variant) => ({
                                                    id: variant.id,
                                                    name: variant.name,
                                                })),
                                                children: [],
                                            },
                                        ].sort(SortByOrder),
                                    },
                                })),
                            );
                        });
                    }
                } catch (e) {
                    //
                }
            },
        }),
        createAndAddTag: builder.mutation<AddTagApiResponse, CreateAndAddTagParams>({
            query: (params) => {
                const formData = JSON.stringify({
                    tag_name: params.tagName,
                });

                return {
                    url: `/sections/${params.id}/tags/`,
                    method: 'POST',
                    body: formData,
                };
            },
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                const { id, documentId, chapterId } = args;

                try {
                    const { data: queryData } = await queryFulfilled;

                    // INVALIDATE DATA
                    invalidateData(dispatch);

                    // ADD TAG TO SINGLE SECTION
                    updateSingleSectionTags({
                        dispatch,
                        id,
                        data: {
                            tagId: queryData.tag_id,
                            tagName: queryData.tag_name,
                        },
                    });

                    // ADD TAG TO SINGLE CHAPTER
                    updateSingleChapterTags({
                        dispatch,
                        chapterId,
                        id,
                        data: {
                            tagId: queryData.tag_id,
                            tagName: queryData.tag_name,
                        },
                    });

                    // ADD TAG TO DOCUMENT
                    dispatch(
                        tagsAdminApi.util.updateQueryData('getAll', { documentId }, (draft) => ({
                            data: [...draft.data, {
                                id: queryData.tag_id,
                                name: queryData.tag_name,
                            }],
                        })),
                    );
                } catch (e) {
                    //
                }
            },
        }),
        addTag: builder.mutation<AddTagApiResponse, AddTagParams>({
            query: (params) => {
                const formData = JSON.stringify({
                    tag_id: params.tagId,
                });

                return {
                    url: `/sections/${params.id}/tags/`,
                    method: 'PATCH',
                    body: formData,
                };
            },
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                const { id, chapterId } = args;

                try {
                    const { data: queryData } = await queryFulfilled;

                    // INVALIDATE DATA
                    invalidateData(dispatch);

                    // ADD TAG TO SINGLE SECTION
                    updateSingleSectionTags({
                        dispatch,
                        id,
                        data: {
                            tagId: queryData.tag_id,
                            tagName: queryData.tag_name,
                        },
                    });

                    // ADD TAG TO SINGLE CHAPTER
                    updateSingleChapterTags({
                        dispatch,
                        chapterId,
                        id,
                        data: {
                            tagId: queryData.tag_id,
                            tagName: queryData.tag_name,
                        },
                    });
                } catch (e) {
                    //
                }
            },
        }),
        deleteTag: builder.mutation<void, DeleteTagParams>({
            query: (params) => {
                const formData = JSON.stringify({
                    tag_id: params.tagId,
                });

                return {
                    url: `/sections/${params.id}/tags/`,
                    method: 'DELETE',
                    body: formData,
                };
            },
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                const { id, chapterId, tagId } = args;

                try {
                    await queryFulfilled;

                    // INVALIDATE DATA
                    invalidateData(dispatch);

                    // DELETE TAG FROM SINGLE SECTION
                    dispatch(
                        sectionsAdminApi.util.updateQueryData('getById', id, (draft) => ({
                            data: {
                                ...draft.data,
                                tags: draft.data.tags.filter((tag) => tag.id !== tagId),
                            },
                            meta: draft.meta,
                        })),
                    );

                    // DELETE TAG FROM SINGLE CHAPTER
                    dispatch(
                        chaptersAdminApi.util.updateQueryData('getById', chapterId, (draft) => ({
                            data: {
                                ...draft.data,
                                sections: draft.data.sections.map((section) => {
                                    if (section.id === id) {
                                        return {
                                            ...section,
                                            tags: section.tags.filter((tag) => tag.id !== tagId),
                                        };
                                    }

                                    return section;
                                }),
                            },
                            meta: {
                                ...draft.meta,
                                tags: extractSectionsTags(draft.data.sections.reduce((acc: any, section: any) => {
                                    const { variants } = section;

                                    return [
                                        ...acc,
                                        ...section.tags.filter((tag: any) => tag.id !== tagId),
                                        ...variants.reduce((innerAcc: any, variant: any) => {
                                            const { tags } = variant;

                                            return [...innerAcc, ...tags];
                                        }, []),
                                    ];
                                }, [])),
                            },
                        })),
                    );
                } catch (e) {
                    //
                }
            },
        }),
        addVendor: builder.mutation<AddVendorApiResponse, AddVendorParams>({
            query: (params) => {
                const formData = JSON.stringify({
                    vendor_id: params.vendorId,
                });

                return {
                    url: `/sections/${params.id}/vendors/`,
                    method: 'PATCH',
                    body: formData,
                };
            },
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                try {
                    const { data: queryData } = await queryFulfilled;

                    // INVALIDATE DATA
                    invalidateData(dispatch);

                    // ADD VENDOR TO SINGLE SECTION
                    dispatch(
                        sectionsAdminApi.util.updateQueryData('getById', id, (draft) => ({
                            data: {
                                ...draft.data,
                                vendors: [...draft.data.vendors, {
                                    id: queryData.vendor_id,
                                    name: queryData.vendor_name,
                                    company: queryData.vendor_company,
                                    address: queryData.vendor_address,
                                    email: queryData.vendor_email,
                                    phone: queryData.vendor_phone,
                                    isVisible: queryData.vendor_is_visible,
                                }].sort(SortById),
                            },
                            meta: draft.meta,
                        })),
                    );
                } catch (e) {
                    //
                }
            },
        }),
        deleteVendor: builder.mutation<void, DeleteVendorParams>({
            query: (params) => {
                const formData = JSON.stringify({
                    vendor_id: params.vendorId,
                });

                return {
                    url: `/sections/${params.id}/vendors/`,
                    method: 'DELETE',
                    body: formData,
                };
            },
            async onQueryStarted({ id, vendorId }, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    // INVALIDATE DATA
                    invalidateData(dispatch);

                    // DELETE VENDOR FROM SINGLE SECTION
                    dispatch(
                        sectionsAdminApi.util.updateQueryData('getById', id, (draft) => ({
                            data: {
                                ...draft.data,
                                vendors: draft.data.vendors.filter((vendor) => vendor.id !== vendorId),
                            },
                            meta: draft.meta,
                        })),
                    );
                } catch (e) {
                    //
                }
            },
        }),
    }),
});

export const {
    useGetByIdQuery: useGetSectionByIdAdmin,
    useLazyGetByIdQuery: useGetSectionByIdAdminLazy,
    useHistoryQuery: useSectionHistory,
    useCreateMutation: useCreateSection,
    useUpdateMutation: useUpdateSection,
    useDeleteMutation: useDeleteSection,
    useCopyMutation: useCopySection,
    useCreateAndAddTagMutation: useCreateAndAddTagToSection,
    useAddTagMutation: useAddTagToSection,
    useDeleteTagMutation: useDeleteTagFromSection,
    
    
} = sectionsAdminApi;
